/**
 * The editing surface's own stylesheet.
 *
 * It lives here rather than in `src/styles.css` for two reasons. The first is
 * ownership: this directory is the editor, and a component that cannot be
 * mounted without an edit to a global file three directories away is a component
 * that will eventually be mounted without it. The second is scope — every rule
 * below is under `.mx-document`, so nothing here can reach the admin shell.
 *
 * **Colours are derived, not named.** Almost every value is `currentColor` mixed
 * with the surface, which means the whole sheet follows the app's light and dark
 * tokens without knowing they exist — and without duplicating the token names,
 * which would go stale the first time `styles.css` changes one. Only the three
 * *named* callout variants are literal, because "warning" has to be orange in
 * both themes or it is not a warning; each is a mid-tone used at low alpha,
 * which reads on white and on a near-black page alike. The unnamed default is
 * `currentColor`, which is the shell's monochrome rule applied here: a callout
 * that has not said what kind it is is not saying anything with colour.
 *
 * React 19 hoists a `<style>` with an `href` and a `precedence` into the head and
 * deduplicates by `href`, so two editors on one page produce one stylesheet.
 */

const CSS = `
.mx-document-body {
  outline: none;
  color: inherit;
  font-size: 1rem;
  line-height: 1.7;
  word-break: break-word;
}

.mx-document-body > * + * { margin-top: 0.85em; }

.mx-document-body h1,
.mx-document-body h2,
.mx-document-body h3,
.mx-document-body h4,
.mx-document-body h5,
.mx-document-body h6 {
  font-weight: 600;
  line-height: 1.3;
  margin-top: 1.6em;
}
.mx-document-body h1 { font-size: 1.75rem; }
.mx-document-body h2 { font-size: 1.4rem; }
.mx-document-body h3 { font-size: 1.2rem; }
.mx-document-body h4,
.mx-document-body h5,
.mx-document-body h6 { font-size: 1rem; }
.mx-document-body > :first-child { margin-top: 0; }

.mx-document-body ul,
.mx-document-body ol { padding-left: 1.4em; }
.mx-document-body ul { list-style: disc; }
.mx-document-body ol { list-style: decimal; }
.mx-document-body li > * + * { margin-top: 0.4em; }
.mx-document-body li p { margin: 0; }

/* Task list — the checkbox is the item's state, so it is not a bullet. */
.mx-document-body ul[data-type="taskList"] { list-style: none; padding-left: 0.15em; }
.mx-document-body ul[data-type="taskList"] li { display: flex; gap: 0.55em; align-items: flex-start; }
.mx-document-body ul[data-type="taskList"] li > label { margin-top: 0.28em; user-select: none; }
.mx-document-body ul[data-type="taskList"] li > div { flex: 1 1 auto; min-width: 0; }
.mx-document-body ul[data-type="taskList"] input[type="checkbox"] { cursor: pointer; }
.mx-document-body ul[data-type="taskList"] li[data-checked="true"] > div { opacity: 0.6; }

.mx-document-body blockquote {
  border-left: 3px solid color-mix(in srgb, currentColor 25%, transparent);
  padding-left: 1em;
  color: color-mix(in srgb, currentColor 75%, transparent);
}

.mx-document-body code {
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 0.875em;
  background: color-mix(in srgb, currentColor 8%, transparent);
  border-radius: 0.25rem;
  padding: 0.1em 0.3em;
}

.mx-document-body pre {
  background: color-mix(in srgb, currentColor 6%, transparent);
  border: 1px solid color-mix(in srgb, currentColor 12%, transparent);
  border-radius: 0.5rem;
  padding: 0.85em 1em;
  overflow-x: auto;
}
.mx-document-body pre code { background: none; padding: 0; font-size: 0.85em; }

.mx-document-body hr {
  border: none;
  border-top: 1px solid color-mix(in srgb, currentColor 20%, transparent);
}

.mx-document-body img { max-width: 100%; height: auto; border-radius: 0.5rem; }

/* Tables are fixed-layout because column widths are not stored: markdown has no
   spelling for one, so the colwidth attribute was removed from the schema and
   resizing must stay off. Fixed layout keeps the columns even without it. */
.mx-document-body table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  overflow: hidden;
}
.mx-document-body th,
.mx-document-body td {
  border: 1px solid color-mix(in srgb, currentColor 20%, transparent);
  padding: 0.4em 0.6em;
  vertical-align: top;
  text-align: left;
}
.mx-document-body th { background: color-mix(in srgb, currentColor 7%, transparent); font-weight: 600; }
.mx-document-body .selectedCell::after {
  content: "";
  position: absolute;
  inset: 0;
  background: color-mix(in srgb, currentColor 10%, transparent);
  pointer-events: none;
}

.mx-document-body aside[data-callout] {
  --mx-callout: currentColor;
  /* The rule is a separate variable so the ink default can be drawn back to a
     hairline while a named variant keeps its accent at full strength — an
     orange warning has to look like one, and a plain aside does not. */
  --mx-callout-rule: color-mix(in srgb, currentColor 35%, transparent);
  border-left: 3px solid var(--mx-callout-rule);
  background: color-mix(in srgb, var(--mx-callout) 6%, transparent);
  border-radius: 0 0.375rem 0.375rem 0;
  padding: 0.75em 1em;
}
.mx-document-body aside[data-callout][data-variant="tip"] {
  --mx-callout: #079455;
  --mx-callout-rule: #079455;
}
.mx-document-body aside[data-callout][data-variant="warning"] {
  --mx-callout: #dc6803;
  --mx-callout-rule: #dc6803;
}
.mx-document-body aside[data-callout][data-variant="danger"] {
  --mx-callout: #d92d20;
  --mx-callout-rule: #d92d20;
}
.mx-document-body aside[data-callout] > * + * { margin-top: 0.6em; }
.mx-document-body aside[data-callout]::before {
  content: attr(data-variant);
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--mx-callout);
  margin-bottom: 0.35em;
}

/* An embed and a live-data placeholder are atoms whose renderHTML emits an empty
   element — the URL and the source live in attributes. Painting them from those
   attributes is what makes an otherwise invisible node visible, and keeps the
   rendering honest: what is shown is exactly what is stored. */
.mx-document-body div[data-embed],
.mx-document-body div[data-live-data] {
  border: 1px dashed color-mix(in srgb, currentColor 30%, transparent);
  border-radius: 0.5rem;
  padding: 0.7em 0.9em;
  font-size: 0.875rem;
  color: color-mix(in srgb, currentColor 75%, transparent);
  user-select: none;
  overflow-wrap: anywhere;
}
.mx-document-body div[data-embed]::before { content: "⧉ " attr(url); }
.mx-document-body div[data-embed][title]:not([title=""])::after {
  content: " — " attr(title);
}
.mx-document-body div[data-live-data]::before {
  content: "⚡ live data · " attr(source) " · " attr(format);
}
.mx-document-body .ProseMirror-selectednode {
  outline: 2px solid var(--ring-color-brand, currentColor);
  outline-offset: 2px;
}

.mx-document-body a { color: inherit; text-decoration: underline; text-underline-offset: 2px; }

/* §4.6 — a link that leaves the application says so, in both directions: the
   dotted underline while reading, and the glyph that survives a printout. */
.mx-document-body .mx-external-link {
  text-decoration-style: dotted;
  text-decoration-thickness: 1.5px;
}
.mx-document-body .mx-external-link-marker {
  font-size: 0.8em;
  vertical-align: 0.25em;
  margin-left: 0.1em;
  opacity: 0.75;
  user-select: none;
}

/* Placeholder, from @tiptap/extensions. */
.mx-document-body p.is-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
  color: color-mix(in srgb, currentColor 45%, transparent);
}

/* Someone else's caret. The label is the only thing that names them inline, so
   it is always visible rather than shown on hover — a caret with no name is a
   distraction, not presence. */
.mx-document-body .collaboration-carets__caret {
  position: relative;
  border-left: 1px solid;
  border-right: 1px solid;
  margin-left: -1px;
  margin-right: -1px;
  pointer-events: none;
  word-break: normal;
}
.mx-document-body .collaboration-carets__label {
  position: absolute;
  top: -1.4em;
  left: -1px;
  border-radius: 0.25rem 0.25rem 0.25rem 0;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.15rem 0.3rem;
  white-space: nowrap;
  user-select: none;
}
.mx-document-body .collaboration-carets__selection {
  border-radius: 0.15rem;
  mix-blend-mode: multiply;
}
`;

/**
 * Mounted by `DocumentEditor`, so nothing else has to remember to.
 */
export function EditorStyles() {
  return (
    <style href="matchbox-document-editor" precedence="medium">
      {CSS}
    </style>
  );
}
